import { Component, EventEmitter, Input, Output, OnDestroy } from '@angular/core';
import { Labels } from 'src/app/app.models';
import { BankForm, BankGFFPField } from '../bank-account.models';
import { FormBuilder, FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';

// Example of the BankGFFPField interface
// export interface BankGFFPField {
//   field: string - targetAccount.bankCode
//   dataType: string - "string"
//   required: boolean - boolean
//   fieldLabel: string - Routing Number
//   displayOrder: string // this value is a stringified number
//   fieldMin: string - minLength
//   fieldMax: string - maxLength
//   regEx: string - regex
// }

@Component({
    selector: 'app-bank-account-add',
    templateUrl: './bank-account-add.component.html',
    styleUrls: ['./bank-account-add.component.scss'],
    standalone: false
})
export class BankAccountAddComponent implements OnDestroy {
  @Output() public submitBankAccountEvent: EventEmitter<BankForm> = new EventEmitter<BankForm>()
  @Input() labels: Labels

  private accountNumberSubscription: Subscription | undefined;

  private _bankGFFPFields: BankGFFPField[]
  @Input() set bankGFFPFields(bankGFFPFields: BankGFFPField[]) {
    if (bankGFFPFields.length > 0) {
      let bankFields = bankGFFPFields.map((bankField) => {
        return {
          ...bankField,
          field: this.formatBankField(bankField.field)
        }
      })
      this._bankGFFPFields = this.sortBankFields(bankFields);
      this.buildBankAccountForm()
    }
  }

  public optionConfig = [
    {
      id: '32',
      title: 'Savings',
      subtitle: '',
      optionMetadata: '32'
    },
    {
      id: '22',
      title: 'Checking',
      subtitle: '',
      optionMetadata: '22'
    }
  ]

  public bankAccountForm: FormGroup

  constructor(private fb: FormBuilder) {}

  public get bankGFFPFields() {
    return this._bankGFFPFields
  }

  private accountNumberMatchValidator(): ValidationErrors | null {
    if (!this.bankAccountForm) return null;
    
    const accountNumber = this.bankAccountForm.get('targetAccountAccountNumber')?.value;
    const confirmAccountNumber = this.bankAccountForm.get('targetAccountAccountNumberVerification')?.value;
    
    if (accountNumber && confirmAccountNumber && accountNumber !== confirmAccountNumber) {
      return { accountNumberMismatch: true };
    }
    
    return null;
  }

  private buildBankAccountForm(): void {
    const formGroup: { [key: string]: any } = {};
    this.bankGFFPFields.forEach((field) => {
      const newFormControl = new FormControl('', {
        validators: this.mapFormValidators(field),
        updateOn: 'change'
      });
      formGroup[field.field] = newFormControl;
    });
    
    this.bankAccountForm = this.fb.group(formGroup);

    // Add account number match validation
    if (this.bankAccountForm.get('targetAccountAccountNumber') && 
        this.bankAccountForm.get('targetAccountAccountNumberVerification')) {
      this.bankAccountForm.get('targetAccountAccountNumberVerification')?.addValidators(
        () => this.accountNumberMatchValidator()
      );

      // Update validation when account number changes
      this.accountNumberSubscription = this.bankAccountForm.get('targetAccountAccountNumber')?.valueChanges.subscribe(() => {
        this.bankAccountForm.get('targetAccountAccountNumberVerification')?.updateValueAndValidity();
      });
    }
  }

  private mapFormValidators(field: BankGFFPField): any[] {
    let validators = []
    if (field.required) {
      validators.push(Validators.required)
    }
    if (field?.fieldMin) {
      validators.push(Validators.minLength(Number(field.fieldMin)))
    }
    if (field?.fieldMax) {
      validators.push(Validators.maxLength(Number(field.fieldMax)))
    }
    if (field?.regEx) {
      validators.push(Validators.pattern(field.regEx))
    }
    return validators
  }

  private formatBankField(field: string): string {
    if (!field.includes('.')) {
      return field;
    }

    return field
      .split('.')
      .map((part, index) => {
        return index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1);
      })
      .join('');
  }

  private sortBankFields(bankFields: BankGFFPField[]): BankGFFPField[] {
    const priorityOrder = ['8', '9', '5', '7']; // These are the displayOrder values that we want to prioritize, in this order #MAWX-3136
    const priorityFields: BankGFFPField[] = [];
    const otherFields: BankGFFPField[] = [];

    bankFields.forEach(field => {
      if (priorityOrder.includes(field.displayOrder)) {
        priorityFields.push(field);
      } else {
        otherFields.push(field);
      }
    });

    priorityFields.sort((a, b) => {
      return priorityOrder.indexOf(a.displayOrder) - priorityOrder.indexOf(b.displayOrder);
    });

    otherFields.sort((a, b) => {
      return Number(a.displayOrder) - Number(b.displayOrder);
    });

    return [...priorityFields, ...otherFields];
  }

  ngOnDestroy(): void {
    if (this.accountNumberSubscription) {
      this.accountNumberSubscription.unsubscribe();
    }
  }

  public onSubmit(formValue: BankForm): void { // Todo: Type
    this.submitBankAccountEvent.emit(formValue)
  }
}
